import React, { useEffect, useState } from 'react';
import {
  Accessibility,
  X,
  Type,
  Contrast,
  BookOpen,
  Sparkles,
  Underline,
  RotateCcw,
} from 'lucide-react';
import { useAccessibility } from '../hooks/useAccessibility';

const SCALES = [
  { value: 0.9, label: 'Small' },
  { value: 1, label: 'Default' },
  { value: 1.15, label: 'Large' },
  { value: 1.3, label: 'XL' },
];

function ToggleRow({ icon: Icon, title, description, checked, onChange, testid }) {
  return (
    <label
      data-testid={testid}
      className="flex items-start gap-3 p-3 rounded-xl"
      style={{
        background: checked ? 'var(--tf-bg-secondary)' : 'transparent',
        border: '1px solid var(--tf-card-border)',
        cursor: 'pointer',
      }}
    >
      <div
        className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5"
        style={{
          background: checked ? 'var(--tf-purple)' : '#fff',
          color: checked ? '#fff' : 'var(--tf-purple)',
          border: checked ? 'none' : '1px solid var(--tf-purple-light)',
        }}
      >
        <Icon size={16} />
      </div>
      <div className="flex-1 min-w-0">
        <p
          className="font-body font-bold"
          style={{ fontSize: 13.5, color: 'var(--tf-text)' }}
        >
          {title}
        </p>
        <p
          className="font-body"
          style={{
            fontSize: 11.5,
            color: 'var(--tf-text-secondary)',
            marginTop: 2,
            lineHeight: 1.45,
          }}
        >
          {description}
        </p>
      </div>
      <input
        type="checkbox"
        className="sr-only"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
      />
      <span
        aria-hidden
        className="relative inline-block flex-shrink-0"
        style={{
          width: 38,
          height: 22,
          background: checked ? 'var(--tf-purple)' : '#d6d2dc',
          borderRadius: 9999,
          transition: 'background 0.2s ease',
          marginTop: 8,
        }}
      >
        <span
          style={{
            position: 'absolute',
            top: 2,
            left: checked ? 18 : 2,
            width: 18,
            height: 18,
            background: '#fff',
            borderRadius: 9999,
            transition: 'left 0.2s ease',
            boxShadow: '0 1px 2px rgba(0,0,0,0.2)',
          }}
        />
      </span>
    </label>
  );
}

export default function AccessibilityFAB() {
  const { settings, update, reset } = useAccessibility();
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        data-testid="a11y-fab"
        aria-label="Open accessibility settings"
        className="tf-no-print fixed z-40 inline-flex items-center justify-center rounded-full"
        style={{
          right: 16,
          bottom: 'calc(env(safe-area-inset-bottom, 0px) + 138px)',
          width: 48,
          height: 48,
          background: '#fff',
          color: 'var(--tf-purple)',
          border: '1.5px solid var(--tf-purple-light)',
          boxShadow: '0 8px 24px rgba(139,123,177,0.18)',
          cursor: 'pointer',
        }}
      >
        <Accessibility size={20} strokeWidth={2.2} />
      </button>

      {open && (
        <div
          className="tf-no-print fixed inset-0 z-50 flex items-stretch justify-end"
          role="dialog"
          aria-modal="true"
          aria-label="Accessibility settings"
          data-testid="a11y-panel"
        >
          {/* Backdrop */}
          <button
            type="button"
            aria-label="Close accessibility settings"
            onClick={() => setOpen(false)}
            className="absolute inset-0"
            style={{
              background: 'rgba(42,42,53,0.45)',
              backdropFilter: 'blur(3px)',
              WebkitBackdropFilter: 'blur(3px)',
              border: 'none',
              cursor: 'pointer',
            }}
          />

          <aside
            className="relative w-full sm:max-w-sm overflow-y-auto"
            style={{
              background: 'var(--tf-bg)',
              boxShadow: '-12px 0 40px rgba(42,42,53,0.18)',
              animation: 'tf-fade-up 0.25s ease-out',
            }}
          >
            <div
              className="sticky top-0 z-10 flex items-center justify-between px-5 py-4"
              style={{
                background: 'rgba(253,252,254,0.95)',
                backdropFilter: 'blur(10px)',
                borderBottom: '1px solid var(--tf-card-border)',
              }}
            >
              <div className="flex items-center gap-2">
                <Accessibility size={16} color="var(--tf-purple)" />
                <span
                  className="font-bold"
                  style={{ color: 'var(--tf-text)', fontSize: 14 }}
                >
                  Accessibility
                </span>
              </div>
              <button
                type="button"
                onClick={() => setOpen(false)}
                aria-label="Close"
                data-testid="a11y-close"
                className="w-9 h-9 rounded-full inline-flex items-center justify-center"
                style={{
                  background: 'var(--tf-bg-secondary)',
                  border: 'none',
                  color: 'var(--tf-text)',
                  cursor: 'pointer',
                }}
              >
                <X size={16} />
              </button>
            </div>

            <div className="px-5 py-5 pb-10">
              <p
                className="font-body"
                style={{
                  fontSize: 13,
                  color: 'var(--tf-text-secondary)',
                  lineHeight: 1.55,
                }}
              >
                Adjust the site to feel easier to use. Settings are saved in this browser.
              </p>

              {/* Text size */}
              <div className="mt-6">
                <div className="flex items-center gap-2 mb-3">
                  <Type size={14} color="var(--tf-purple)" />
                  <p
                    className="font-body font-bold"
                    style={{
                      fontSize: 11.5,
                      letterSpacing: '0.16em',
                      color: 'var(--tf-purple)',
                    }}
                  >
                    TEXT SIZE
                  </p>
                </div>
                <div className="grid grid-cols-4 gap-2">
                  {SCALES.map((s) => {
                    const active = settings.fontScale === s.value;
                    return (
                      <button
                        key={s.value}
                        type="button"
                        onClick={() => update({ fontScale: s.value })}
                        data-testid={`a11y-text-${s.label.toLowerCase()}`}
                        className="py-2 rounded-xl font-bold"
                        style={{
                          background: active ? 'var(--tf-purple)' : '#fff',
                          color: active ? '#fff' : 'var(--tf-text-secondary)',
                          border: `1px solid ${active ? 'var(--tf-purple)' : 'var(--tf-card-border)'}`,
                          fontSize: 12.5,
                          cursor: 'pointer',
                        }}
                      >
                        {s.label}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Toggles */}
              <div className="mt-6 flex flex-col gap-2.5">
                <ToggleRow
                  icon={Contrast}
                  title="High contrast"
                  description="Stronger text and edges on a near-white background."
                  checked={settings.highContrast}
                  onChange={(v) => update({ highContrast: v })}
                  testid="a11y-toggle-contrast"
                />
                <ToggleRow
                  icon={BookOpen}
                  title="Dyslexia-friendly font"
                  description="Switches to a heavier, more readable sans-serif throughout."
                  checked={settings.dyslexiaFont}
                  onChange={(v) => update({ dyslexiaFont: v })}
                  testid="a11y-toggle-dyslexia"
                />
                <ToggleRow
                  icon={Sparkles}
                  title="Reduce motion"
                  description="Disables breathing animations, fades and other movement."
                  checked={settings.reduceMotion}
                  onChange={(v) => update({ reduceMotion: v })}
                  testid="a11y-toggle-motion"
                />
                <ToggleRow
                  icon={Underline}
                  title="Underline links"
                  description="Adds an underline to every link so they stand out."
                  checked={settings.underlineLinks}
                  onChange={(v) => update({ underlineLinks: v })}
                  testid="a11y-toggle-underline"
                />
              </div>

              <button
                type="button"
                onClick={reset}
                data-testid="a11y-reset"
                className="mt-6 inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full font-bold"
                style={{
                  background: 'var(--tf-bg-secondary)',
                  color: 'var(--tf-text-secondary)',
                  border: 'none',
                  fontSize: 12,
                  cursor: 'pointer',
                }}
              >
                <RotateCcw size={13} />
                Reset to defaults
              </button>

              <p
                className="font-body mt-6"
                style={{ fontSize: 11.5, color: 'var(--tf-text-muted)', lineHeight: 1.55 }}
              >
                Your browser may also respect system settings such as &quot;Reduce motion&quot;,
                large text, and dark mode automatically.
              </p>
            </div>
          </aside>
        </div>
      )}
    </>
  );
}
